import { useTimezone } from '@app/core/hooks/useTimezone';
import { LocaleDate } from '@mi-front/ui';
import { Box } from '@mui/material';
import moment from 'moment-timezone';
import React from 'react';
import { useIntl } from 'react-intl';
import { dateSelectorPresetMessages } from '../date-range-selector/date-selector-messages';
import { isTodayInTimezone, isYesterdayInTimezone } from './ldate-utils';

interface Props extends React.ComponentProps<typeof LocaleDate> {
  tz?: string;
  /** When true, uppercases the first character of the formatted date (e.g. "lun." → "Lun."). */
  capitalize?: boolean;
  /** When true, shows localized "Hoy" / "Ayer" for today and yesterday in the active timezone. */
  showTodayYesterday?: boolean;
}

/** Locale-aware date with optional timezone, today/yesterday labels, and first-letter capitalization. */
export function LDate({
  value,
  tz: passedTz,
  capitalize = false,
  showTodayYesterday = false,
  ...rest
}: Props) {
  const contextTz = useTimezone();
  const tz = passedTz || contextTz;
  const { formatMessage: $t } = useIntl();
  const title = tz ? moment(value).tz(tz).format('LLLL (Z)') : moment(value).format('LLLL (Z)');

  if (showTodayYesterday) {
    if (isTodayInTimezone(value, tz || undefined)) {
      return (
        <Box component="span" title={title}>
          {$t(dateSelectorPresetMessages.today)}
        </Box>
      );
    }

    if (isYesterdayInTimezone(value, tz || undefined)) {
      return (
        <Box component="span" title={title}>
          {$t(dateSelectorPresetMessages.yesterday)}
        </Box>
      );
    }
  }

  return (
    <Box
      component="span"
      title={title}
      sx={capitalize ? { span: { textTransform: 'capitalize' } } : undefined}
    >
      <LocaleDate timeZone={tz || undefined} value={value} {...rest} />
    </Box>
  );
}
